import { useMemo } from "react";
import { Dialog } from "@/components/ui/Dialog";
import { PersonSearchList } from "@/components/board/PersonPicker";
import { useUpdateBoard } from "@/hooks/useUpdateBoard";
import { useBoardPeople } from "@/hooks/useBoardPeople";
import { showToast } from "@/lib/toast";
import { t } from "@/lib/strings";
import type { ViewBoard } from "@/lib/boardView";
import type { UUID } from "@/lib/types";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  board: ViewBoard;
  boardId: UUID;
  workspaceId: UUID;
}

/**
 * Admin panel for an Ideenbox (kind='ideas'): pick the DEFAULT ASSIGNEE — the
 * colleague who receives every idea whose submitter left the assignee empty.
 * Stored in the board's `view_config` JSONB (no migration); saved via the plain
 * board PATCH (read-modify-write so the other view_config keys survive).
 * Re-assignment itself needs nothing here — any workspace member re-assigns by
 * editing the Gutachter cell in the board view.
 */
export function IdeenboxSettingsDialog({ open, onOpenChange, board, boardId, workspaceId }: Props) {
  const update = useUpdateBoard();
  const people = useBoardPeople(open ? boardId : undefined);
  const currentId = board.viewConfig?.default_assignee_user_id ?? null;

  // The default assignee is the fallback owner of unassigned ideas, so it must be
  // a real member — guests (who only see their own rows) are filtered out.
  const guestIds = useMemo(
    () => (people.data ?? []).filter((p) => p.is_guest).map((p) => p.user_id),
    [people.data],
  );
  const current = useMemo(
    () => (currentId ? ((people.data ?? []).find((p) => p.user_id === currentId) ?? null) : null),
    [currentId, people.data],
  );

  function save(next: UUID | null) {
    update.mutate(
      {
        boardId,
        workspaceId,
        payload: { view_config: { ...board.viewConfig, default_assignee_user_id: next } },
      },
      {
        onSuccess: () => showToast(t.ideenSettingsSaved, "success"),
        onError: (e) => showToast(e instanceof Error ? e.message : t.unknownError, "error"),
      },
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => !update.isPending && onOpenChange(o)}
      title={t.ideenSettingsTitle}
      description={t.ideenSettingsDescription}
      width={420}
    >
      <div style={{ marginTop: 4 }}>
        <div style={{ fontSize: 12, fontWeight: 500, color: "var(--ink-2)", marginBottom: 6 }}>
          {t.ideenDefaultAssigneeLabel}
        </div>

        {current ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "8px 12px",
              borderRadius: 8,
              border: "1px solid var(--hairline)",
              marginBottom: 10,
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: 13, color: "var(--ink-1)" }}>
                {current.display_name || current.username || current.email}
              </div>
              <div style={{ fontSize: 11.5, color: "var(--ink-3)" }}>{current.email}</div>
            </div>
            <button
              type="button"
              onClick={() => save(null)}
              disabled={update.isPending}
              style={{
                fontSize: 12,
                fontWeight: 500,
                color: "var(--ink-2)",
                background: "transparent",
                border: "1px solid var(--hairline)",
                borderRadius: 7,
                padding: "5px 10px",
                cursor: update.isPending ? "wait" : "pointer",
                fontFamily: "Inter Variable, Inter, system-ui, sans-serif",
              }}
            >
              {t.ideenDefaultAssigneeRemove}
            </button>
          </div>
        ) : (
          <div style={{ fontSize: 12, color: "var(--ink-4)", marginBottom: 10 }}>
            {t.ideenDefaultAssigneeNone}
          </div>
        )}

        <div style={{ fontSize: 11.5, color: "var(--ink-3)", marginBottom: 8 }}>
          {t.ideenDefaultAssigneeHint}
        </div>

        <div style={{ border: "1px solid var(--hairline)", borderRadius: 8, overflow: "hidden" }}>
          <PersonSearchList
            value={currentId ? [currentId] : []}
            allowMultiple={false}
            boardId={boardId}
            excludeUserIds={guestIds}
            onClose={() => {}}
            onChange={(next) => save(next[0] ?? null)}
          />
        </div>
      </div>
    </Dialog>
  );
}
